import {
  useDeleteEncashmentBatchMutation,
  useGetAllEncashmentBatchQuery,
  useRecalculateBatchMutation,
  type EncashmentBatchItem,
} from '@/app/features/employee/emp-payroll';
import ConfirmationModal from '@/components/ConfirmationModal';
import CustomTable from '@/components/CustomTable';
import { DISCLOSER_KEYS, FILTER_KEYS } from '@/constants/keys.constants';
import { useAppDiscloser, useAppFilters } from '@/hooks';
import { EyeIcon, ReloadIcon, TrashIcon } from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { Button, Space, Tooltip, type TableProps } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import ManageEncashment from './_components/LeaveEncashment/ManageEncashment';
import { formatBDT } from './_components/shared/payroll.helpers';

/** Generated, failed (with the reason on hover) or not generated yet. */
const BatchState = ({ batch }: { batch: EncashmentBatchItem }) => {
  if (batch.status === 'FAILED') {
    return (
      <Tooltip title={batch.failed_reason}>
        <span className="status status-error">Failed</span>
      </Tooltip>
    );
  }
  if (batch.status === 'GENERATED') {
    return (
      <Link
        to={`encashment-batch/${batch.id}/disburse-records`}
        className="text-theme-primary underline underline-offset-2"
      >
        Generated
      </Link>
    );
  }
  return <span className="text-white/40 text-sm">Not Generated</span>;
};

/**
 * Leave Encashment: one batch per fiscal-year run. A batch that is not
 * generated yet can be recalculated or deleted; a generated one is opened to
 * edit records and disburse them into payroll.
 */
export default function LeaveEncashment() {
  const navigate = useNavigate();
  /** The batch whose recalculation is running, for its spinner. */
  const [recalculatingId, setRecalculatingId] = useState<string | null>(null);

  const { page, limit, skip, keyword, filters, handlePagination } =
    useAppFilters(FILTER_KEYS.PAYROLL_LEAVE);
  const [config, handleOpen, handleClose] = useAppDiscloser<
    never,
    EncashmentBatchItem
  >(DISCLOSER_KEYS.PAYROLL_LEAVE);

  const { data, isLoading, isFetching } = useGetAllEncashmentBatchQuery({
    params: { skip, limit, search: keyword, ...filters },
  });

  const [recalculateBatch] = useRecalculateBatchMutation();
  const [deleteEncashmentBatch, { isLoading: deleting }] =
    useDeleteEncashmentBatchMutation();

  const handleDeleteConfirm = async () => {
    if (!config.data?.id) return;
    try {
      await deleteEncashmentBatch({ id: config.data.id }).unwrap();
      toast('success', 'Encashment batch deleted successfully');
      handleClose();
    } catch (error) {
      toast(
        'error',
        getErrorMessage(error, 'Failed to delete encashment batch'),
      );
    }
  };

  const handleRecalculate = async (id: string) => {
    setRecalculatingId(id);
    try {
      await recalculateBatch({ id }).unwrap();
      toast('success', 'Encashment batch recalculated successfully');
    } catch (error) {
      toast(
        'error',
        getErrorMessage(error, 'Failed to recalculate encashment batch'),
      );
    } finally {
      setRecalculatingId(null);
    }
  };

  const columns: TableProps<EncashmentBatchItem>['columns'] = [
    {
      title: 'Batch Name',
      dataIndex: 'batch_name',
      render: (val: string) => val || 'n/a',
    },
    {
      title: 'Year',
      dataIndex: 'fiscal_year_start',
      align: 'center',
      render: (val: string) => (val ? dayjs(val).format('YYYY') : 'n/a'),
    },
    {
      title: 'Number Of Employee',
      dataIndex: 'total_employees',
      align: 'center',
      render: (val?: number) => val ?? 0,
    },
    {
      title: 'Total Amount',
      dataIndex: 'total_amount',
      render: (val: string) => formatBDT(val || null),
    },
    {
      title: 'Salary Sheet',
      dataIndex: 'status',
      align: 'center',
      render: (_, batch) => <BatchState batch={batch} />,
    },
    {
      title: 'Actions',
      align: 'end',
      render: (_, batch) => {
        const isGenerated = batch.status === 'GENERATED';
        const isFailed = batch.status === 'FAILED';
        const canRecalculate = batch.status === 'NOT_GENERATED';
        return (
          <Space>
            <Tooltip title={canRecalculate ? 'Recalculate' : ''}>
              <Button
                size="small"
                loading={recalculatingId === batch.id}
                disabled={!canRecalculate}
                onClick={() => canRecalculate && handleRecalculate(batch.id)}
                className="action-btn disabled:!opacity-50"
                icon={<ReloadIcon size={20} />}
              />
            </Tooltip>
            <Tooltip title={isGenerated ? '' : 'Delete batch'}>
              <Button
                size="small"
                disabled={isGenerated}
                onClick={() =>
                  !isGenerated && handleOpen({ type: 'DELETE', data: batch })
                }
                className="action-btn disabled:!opacity-50"
                icon={<TrashIcon size={20} />}
              />
            </Tooltip>
            <Tooltip title={isFailed ? '' : 'Open batch'}>
              <Button
                size="small"
                disabled={isFailed}
                onClick={() =>
                  !isFailed && navigate(`encashment-batch/${batch.id}`)
                }
                className="action-btn disabled:!opacity-50"
                icon={<EyeIcon size={20} />}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <CustomTable<EncashmentBatchItem>
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        dataSource={data?.data?.data || []}
        onPaginate={handlePagination}
        customTitle="Encashment List"
        tableRootClassName="!bg-transparent !border-none !p-0"
        loading={isLoading || isFetching}
        total={data?.data?.total || 0}
      />

      <ManageEncashment
        open={config.open && config.type !== 'DELETE'}
        handleClose={handleClose}
      />

      <ConfirmationModal
        loading={deleting}
        handleClose={handleClose}
        handleSubmit={handleDeleteConfirm}
        open={config.open && config.type === 'DELETE'}
        buttonName="Delete"
        title="Delete Encashment Batch"
        subTitle={`Are you sure you want to delete "${config.data?.batch_name}"? This action cannot be undone.`}
      />
    </div>
  );
}
